import {
  Active,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DroppableContainer,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React, { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';
import useDefaultBuilderSchemaId from '../../pages/Formulas/utils/useDefaultBuilderSchemaId';
import { IFilterFieldsFragment } from '../../utils/apollo/graphqlSchema';
import { getClosestCollision } from '../DndGrid/utils';
import DictionaryEntityOverlay from '../QuickAccessDictionary/DictionaryEntityOverlay';
import { DND_BASE_LAYER_Z_INDEX } from './constants';
import { DndEntityInternalContext } from './context';
import { IDndEntityPrivateContext, IDropzoneErrors } from './types';
import useDndEntityHandler from './useDndEntityHandler';
import { getDndActiveItemData } from './utils';
import { EntityDropzoneSchema } from './validation';

interface IProps {
  children: ReactNode;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.15);
  z-index: ${DND_BASE_LAYER_Z_INDEX - 2};
  pointer-events: none;
`;

const DragOverlayWrapper = styled.div`
  pointer-events: none;
`;

const DndEntity = ({ children }: IProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<Active | null>(null);
  const [popoverId, setPopoverId] = useState<string | null>(null);
  const [defaultFilter, setDefaultFilter] =
    useState<IFilterFieldsFragment | null>(null);
  const [dropzoneErrors, setDropzoneErrors] = useState<IDropzoneErrors>({});

  const defaultBuilderSchemaId = useDefaultBuilderSchemaId();
  const { selectEntity } = useDndEntityHandler(defaultBuilderSchemaId);

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    if (event?.active) {
      setActiveItem(event.active);
    }

    // set the cursor to grabbing for overlay
    document.body.style.cursor = 'grabbing';
  };

  const handleDragEnd = (ev: DragEndEvent) => {
    try {
      const collision = getClosestCollision(ev.collisions);

      if (collision) {
        const parseResult = EntityDropzoneSchema.safeParse(collision);

        if (parseResult.success) {
          const collisionDescriptor = parseResult.data;
          const data =
            collisionDescriptor.data?.droppableContainer?.data?.current?.data;
          const node = ev.active.data.current?.data;

          if (data) {
            if ('id' in data && data.id && dropzoneErrors[data.id]) {
              // do not execute if there is an error for this dropzone
              return;
            }
            setDropzoneErrors({});

            selectEntity({
              node,
              data,
              setDefaultFilter,
              setPopoverId,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error during drag end:', error);
      return;
    } finally {
      setIsDragging(false);
      setActiveItem(null);
      document.body.style.cursor = 'default';
    }
  };

  // use context to pass DnD context (what is currently dragging, can you drop, etc.) to avoid prop-drilling
  // when the subcomponents need to know or modify the DnD state
  const contextValue = useMemo<IDndEntityPrivateContext>(
    () => ({
      activeItem,
      defaultFilter,
      setDefaultFilter,
      isDragging,
      dropzoneErrors,
      setDropzoneErrors,
      popoverId,
      setPopoverId,
    }),
    [
      activeItem,
      defaultFilter,
      setDefaultFilter,
      isDragging,
      dropzoneErrors,
      setDropzoneErrors,
      popoverId,
      setPopoverId,
    ],
  );

  // allows constraining the activation via a delay or distance
  // used for differentiating onClick (which triggers a popup menu)
  // vs onDrag (which triggers the dropzones)
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const sensors = useSensors(pointerSensor);

  const data = getDndActiveItemData(activeItem);

  // Custom rectIntersection that only considers dropzones with the same entity type as active item
  const customRectIntersection: CollisionDetection = (args) => {
    const filteredContainers = args.droppableContainers.filter(
      (container: DroppableContainer) =>
        args.active?.data?.current?.type &&
        container?.data?.current?.accepts.includes(
          args.active.data.current.type,
        ),
    );

    return rectIntersection({
      ...args,
      droppableContainers: filteredContainers,
    });
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={customRectIntersection}
      autoScroll={{
        canScroll: () => true,
        enabled: true,
        activator: 0,
      }}
      sensors={sensors}
    >
      <DndEntityInternalContext.Provider value={contextValue}>
        {children}
        <DragOverlayWrapper>
          {isDragging && data && (
            <DragOverlay dropAnimation={null}>
              <DictionaryEntityOverlay node={data} />
            </DragOverlay>
          )}
        </DragOverlayWrapper>
      </DndEntityInternalContext.Provider>
      {isDragging && <Overlay />}
    </DndContext>
  );
};

export default DndEntity;
